import { z } from 'zod';

import { formatarNumero, paraNumero } from '@/features/historico/tratamento';

/**
 * `horasAposMedicacao` — o intervalo entre a última dose e a coleta.
 *
 * Fica num módulo próprio, ao lado de `rotulos.ts`, porque o campo aparece em
 * DOIS formulários (exame do portfólio e exame externo) e é EXIBIDO no laudo.
 * Três cópias da mesma regra de 0–240 e da mesma ressalva divergiriam na
 * primeira correção — foi o que o `tratamento.ts` do histórico já resolveu
 * para a dose.
 *
 * ⚠️ **É RELATO DO TUTOR, não medição.** "Dei o remédio umas quatro horas
 * antes" é o que produz este número. Ele é critério de recorte da Análise
 * Comparativa, e sem a ressalva colada nele alguém compara duas coortes por
 * "4 h" achando que houve cronômetro. Por isso o auxiliar não é opcional em
 * lugar nenhum: nem no rótulo do campo, nem na linha do laudo.
 */
export const AUXILIAR_HORAS_APOS_MEDICACAO = 'informado pelo tutor';

/** O auxiliar do formulário: diz de onde vem o número e como digitá-lo. */
export const DICA_HORAS_APOS_MEDICACAO =
  'Informado pelo tutor — é relato, não medição. Meia hora vai como 4,5.';

/**
 * `4`, `4,5`, `4.5` — e nada com mais de duas casas.
 *
 * ⚠️ A coluna é `Decimal(5,2)`: `4,567` seria arredondado no banco, e o valor
 * relido não bateria com o digitado.
 */
const DECIMAL = /^\d{1,3}([.,]\d{1,2})?$/;

/** Limite do contrato (`@minimum 0` / `@maximum 240`): dez dias de janela. */
const MAXIMO_HORAS = 240;

/**
 * O campo no formulário é TEXTO, não `type="number"`.
 *
 * O teclado brasileiro entrega vírgula, e `<input type="number">` a descarta
 * silenciosamente — quem digita `4,5` veria o campo esvaziar. Mesmo caminho da
 * dose em `AjusteDeDoseDialogo`.
 */
export const campoDeHorasAposMedicacao = z
  .string()
  .trim()
  .refine((v) => v === '' || DECIMAL.test(v), 'Informe as horas em número — ex.: 4,5')
  .refine(
    (v) => v === '' || (paraNumero(v) >= 0 && paraNumero(v) <= MAXIMO_HORAS),
    `Entre 0 e ${MAXIMO_HORAS} horas`,
  );

/**
 * Texto do campo → o que vai no JSON.
 *
 * Vazio vira `null`, e não `0`: na edição é o que APAGA o valor, e "zero hora
 * após a medicação" é uma afirmação clínica que ninguém fez.
 */
export function horasParaEnvio(texto: string): number | null {
  const limpo = texto.trim();
  return limpo === '' ? null : paraNumero(limpo);
}

/**
 * `4,5 h após a medicação · informado pelo tutor` — ou `null`.
 *
 * ⚠️ `null` significa **não escreva nada**. A linha de detalhe do laudo já é
 * longa, e um "não informado" a mais em cada exame só empurra para baixo o que
 * importa.
 */
export function descreverHorasAposMedicacao(horas: number | null | undefined): string | null {
  return horas === null || horas === undefined
    ? null
    : `${formatarNumero(horas)} h após a medicação · ${AUXILIAR_HORAS_APOS_MEDICACAO}`;
}
